<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useDefaultGridOptions, useDefaultExport, refreshGridAsync, getCreatedRowId } from '@/composables/data-grid'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { cloneCostReportRow, useCostReportRowColumns, useCostReportRowTypes, useLockedCostReportRow } from '@/composables/cost-report'

import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import FileViewer from '@/components/shared/FileViewer.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import CostReportRowEdit from '@/components/accounting/CostReportRowEdit.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  types: {
    type: Array,
    default: null,
  },
})

const isSingleType = props.types?.length === 1
const gridId = `cost-report-rows-grid-${props.project.id}${props.types ? `-${props.types.join('-')}` : ''}`

const i18n = getI18n()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { costReportRowTypeEnum } = accountingStore

const { getTypeChips } = useCostReportRowTypes()
const { isLocked, isInvoiced } = useLockedCostReportRow()
const hasNothingToInvoice = (row) => isInvoiced(row) || !row.is_rebillable
  || (row.type !== costReportRowTypeEnum.COMMENT && !(Number(row.quantity) > 0))
const editItem = ref(null)
const deleteConfirmRow = ref(null)
const showDeleteDialog = computed({
  get: () => deleteConfirmRow.value !== null,
  set: (val) => { if (!val) deleteConfirmRow.value = null },
})
const { gridRef } = useGridRef()
const { exportOptions, onExporting } = useDefaultExport({ fileName: `${props.project.name} - ${i18n.t('common.costReportRow', 2)}` })

accountingStore.fetchCostReportRowTagsAsync({}, true)
accountingStore.fetchCompanyDataAsync({}, true)
planningStore.fetchStaffsAsync({}, true)

const gridDataSource = useODataSource(accountingStore.api.getAbsoluteUrl() + accountingStore.api.costReportRowsUrl, {
  customParamsFn: () => ({ project: props.project.id, ...(props.types ? { type__in: props.types.join(',') } : {}), ...(webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS) ? {} : { staff: webSiteStore.user?.service_id }) }),
})

const columns = [
  {
    type: 'buttons',
    buttons: [
      {
        hint: i18n.t('common.details'),
        icon: 'mdi mdi-magnify',
        visible: (e) => isLocked(e.row?.data),
        onClick: (e) => { editItem.value = { ...e.row.data } },
      },
      {
        hint: i18n.t('common.edit'),
        icon: 'edit',
        visible: (e) => !isLocked(e.row?.data),
        onClick: (e) => { editItem.value = { ...e.row.data } },
      },
      {
        hint: i18n.t('common.delete'),
        icon: 'trash',
        visible: (e) => !isLocked(e.row?.data),
        onClick: (e) => { deleteConfirmRow.value = e.row.data },
      },
      {
        hint: i18n.t('common.clone'),
        icon: 'copy',
        onClick: (e) => {
          editItem.value = cloneCostReportRow(e.row.data)
        },
      },
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '8rem',
  },
  ...useCostReportRowColumns(props.project.currency_ref).filter(column => !(isSingleType && column.dataField === 'type')),
  {
    dataField: 'row_files',
    caption: i18n.t('common.attachment', 2),
    cellTemplate:'fileViewer',
    editCellTemplate: 'fileUploader',
    width: '16rem',
    allowExporting: false,
  },
]

const emit = defineEmits(['row-saved'])

const updateGrid = async(saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  editItem.value = null
  await accountingStore.fetchCostReportRowTagsAsync({}, true)
  gridRef.value.instance.columnOption('tag', 'lookup.dataSource', accountingStore.getLocalizedCostReportRowTags())
  emit('row-saved', props.project.id, props.project.id)
  await refreshGridAsync(gridRef, createdId)
}

const onDownloadRowFileAsync = async(e) => {
  if (e) {
    await accountingStore.downloadCostReportRowFileAsync(e.data.id, e.file.id)
  }
}

const selectedRows = ref([])

const addButtons = [
  { type: costReportRowTypeEnum.TIME, label: i18n.t('common.costReportTime'), icon: 'mdi-clock-plus-outline' },
  { type: costReportRowTypeEnum.EXPENSE, label: i18n.t('common.expense'), icon: 'mdi-cash-plus' },
  { type: costReportRowTypeEnum.COMMENT, label: i18n.t('common.comment'), icon: 'mdi-comment-plus-outline' },
]
  .filter(button => !props.types || props.types.includes(button.type))
  .map(button => ({ ...button, onClick: () => { editItem.value = { project: props.project.id, type: button.type } } }))

const gridOptions = useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: { allowAdding: false, allowUpdating: false, allowDeleting: false },
  selection: { mode: 'multiple', showCheckBoxesMode: 'always' },
  onSelectionChanged: (e) => {
    const invoicedKeys = e.selectedRowsData.filter(hasNothingToInvoice).map(row => row.id)
    if (invoicedKeys.length) {
      e.component.deselectRows(invoicedKeys)
      return
    }
    selectedRows.value = e.selectedRowsData
    accountingStore.setInvoiceSelection(props.project.id, e.selectedRowsData)
  },
  onCellPrepared: (e) => {
    if (e.rowType === 'data' && e.column.command === 'select' && hasNothingToInvoice(e.data)) {
      e.cellElement.querySelector('.dx-select-checkbox')?.classList.add('invisible')
    }
  },
  export: exportOptions,
  onExporting: onExporting,
  clearFilterButton: true,
  addButtons,
  isDetailGrid: true,
})

onBeforeUnmount(() => accountingStore.setInvoiceSelection(props.project.id, []))

const onDeleteConfirmRow = async (row) => {
  await accountingStore.deleteCostReportRowAsync(row.id)
  await gridRef.value.instance.refresh()
  emit('row-saved', props.project.id)
  deleteConfirmRow.value = null
}
</script>
<template>
  <div>
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteConfirmRow(deleteConfirmRow)"
    />
    <q-dialog :model-value="editItem !== null" class="dialog-md" persistent>
      <cost-report-row-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :project="props.project"
        @update="updateGrid"
        @cancel="updateGrid"
      />
    </q-dialog>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #type-cell-template="{ data: templateOptions }">
        <chip-template :values="getTypeChips(templateOptions.data.type)" />
      </template>
      <template #fileViewer="{ data: templateOptions }">
        <file-viewer
          dense
          :cell-info="templateOptions"
          :urls="templateOptions.data.project_urls"
        />
      </template>
      <template #fileUploader="{ data: templateOptions }">
        <file-url-edit
          :cell-info="templateOptions"
          :urls="templateOptions.data.row_urls"
          @download="onDownloadRowFileAsync"
          @update:urls="pendingUrls[templateOptions.key] = $event"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
